export function normalizeFabricText(value) {
  return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
}

export function normalizeFabricSku(value) {
  return normalizeFabricText(value).replace(/[\s\-‐‑–—]/g, '').replaceAll(',', '.');
}

export function matchFabric(entry, query) {
  const text = normalizeFabricText(query);
  if (!text) return { matches: true, variant: null };
  const sku = normalizeFabricSku(query);
  const exact = entry.variants.find(variant => normalizeFabricSku(variant.sku) === sku);
  if (exact) return { matches: true, variant: exact };
  const referenceMatch = entry.variants.some(variant => variant.sku && normalizeFabricSku(variant.sku).startsWith(sku));
  const words = normalizeFabricText(`${entry.title} ${entry.reference} ${entry.description}`);
  return { matches: referenceMatch || text.split(/\s+/).every(word => words.includes(word)), variant: null };
}

export function fabricPrefixFallback(entries, query) {
  const value = normalizeFabricText(query);
  // A cut may be labelled before its exact colour/length SKU exists in Shopify.
  // Only fall back to a known fabric code, never infer a variant or its properties.
  if (!/[-‐‑–—\d]/.test(value)) return [];
  const compact = normalizeFabricSku(value);
  return entries.filter(entry => {
    const reference = normalizeFabricSku(entry.reference);
    return reference.length >= 2 && compact.startsWith(reference) && compact.length > reference.length;
  });
}

function enableFabricButtons(root) {
  root.querySelectorAll('[data-fabric-toggle]').forEach(button => { button.hidden = false; });
}

if (typeof document !== 'undefined') {
  enableFabricButtons(document);
  document.addEventListener('shopify:section:load', event => enableFabricButtons(event.target));
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-fabric-toggle]');
    if (!button) return;
    const card = button.closest('[data-fabric-card]');
    const visible = card.classList.toggle('is-model-visible');
    button.setAttribute('aria-pressed', String(visible));
  });

  class FabricDirectory extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.input = this.querySelector('[data-fabric-search]');
      this.grid = this.querySelector('[data-fabric-grid]');
      this.status = this.querySelector('[data-fabric-status]');
      this.reset = this.querySelector('[data-fabric-reset]');
      this.empty = this.querySelector('[data-fabric-empty]');
      this.fallback = this.querySelector('[data-fabric-fallback]');
      this.error = this.querySelector('[data-fabric-error]');
      this.next = this.querySelector('[data-fabric-next]');
      this.failed = false;
      this.readCards();
      this.input.value = new URL(location.href).searchParams.get('q') || '';
      const options = { signal: this.controller.signal };
      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.filter(true), 120);
      }, options);
      this.querySelector('[data-fabric-form]').addEventListener('submit', event => {
        event.preventDefault();
        clearTimeout(this.timer);
        this.filter(true);
      }, options);
      this.reset.addEventListener('click', () => {
        this.input.value = '';
        this.filter(true);
        this.input.focus();
      }, options);
      this.querySelector('[data-fabric-retry]').addEventListener('click', () => this.loadRemaining(), options);
      window.addEventListener('popstate', () => {
        this.input.value = new URL(location.href).searchParams.get('q') || '';
        this.filter(false);
      }, options);
      this.filter(false);
      this.loadRemaining();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.controller?.abort();
      this.controller = null;
    }

    readCards() {
      this.entries = [...this.grid.querySelectorAll('[data-fabric-card]')].map(card => ({
        card,
        title: card.dataset.title,
        reference: card.dataset.reference,
        description: card.dataset.description,
        variants: JSON.parse(card.querySelector('[data-fabric-variants]').textContent),
        url: card.dataset.productUrl,
      }));
      enableFabricButtons(this);
    }

    filter(updateUrl) {
      const query = this.input.value.trim();
      const matches = new Map(this.entries.map(entry => [entry, matchFabric(entry, query)]));
      const fallbackEntries = !this.loading && !this.failed && ![...matches.values()].some(match => match.matches)
        ? fabricPrefixFallback(this.entries, query) : [];
      let count = 0;
      for (const entry of this.entries) {
        const match = matches.get(entry);
        const visible = match.matches || fallbackEntries.includes(entry);
        entry.card.hidden = !visible;
        if (visible) count += 1;
        const label = entry.card.querySelector('[data-fabric-reference]');
        if (label) label.textContent = match.variant?.sku || entry.reference;
        const url = new URL(entry.url, location.origin);
        if (match.variant) url.searchParams.set('variant', match.variant.id);
        entry.card.querySelectorAll('[data-fabric-link]').forEach(link => {
          link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
        });
      }
      this.reset.hidden = !query;
      this.empty.hidden = count > 0 || this.loading || this.failed;
      this.fallback.hidden = fallbackEntries.length === 0;
      this.status.textContent = this.loading
        ? this.status.dataset.loadingLabel
        : this.status.dataset.countLabel.replace('[count]', String(count));
      if (updateUrl) {
        const url = new URL(location.href);
        if (query) url.searchParams.set('q', query);
        else url.searchParams.delete('q');
        history.replaceState(history.state, '', url);
      }
    }

    async loadRemaining() {
      if (this.loading || !this.next) return;
      const signal = this.controller.signal;
      this.loading = true;
      this.failed = false;
      this.error.hidden = true;
      this.next.hidden = true;
      this.grid.setAttribute('aria-busy', 'true');
      this.filter(false);
      try {
        const seen = new Set();
        while (this.next) {
          const url = new URL(this.next.href, location.origin);
          if (seen.has(url.href)) throw new Error('Repeated catalogue page');
          seen.add(url.href);
          url.searchParams.set('section_id', this.dataset.sectionId);
          const response = await fetch(url, { signal });
          if (!response.ok) throw new Error(`Catalogue ${response.status}`);
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          if (signal.aborted) return;
          const directory = html.querySelector('blankabric-fabric-directory');
          if (!directory) throw new Error('Missing catalogue');
          const ids = new Set(this.entries.map(entry => entry.card.dataset.productId));
          directory.querySelectorAll('[data-fabric-card]').forEach(card => {
            if (!ids.has(card.dataset.productId)) this.grid.append(document.importNode(card, true));
          });
          const next = directory.querySelector('[data-fabric-next]');
          if (next) this.next.href = next.href;
          else { this.next.remove(); this.next = null; }
          this.readCards();
          this.filter(false);
        }
      } catch (error) {
        if (signal.aborted) return;
        this.failed = true;
        this.error.hidden = false;
        if (this.next) this.next.hidden = false;
      } finally {
        this.loading = false;
        if (!signal.aborted) {
          this.grid.removeAttribute('aria-busy');
          this.filter(false);
        }
      }
    }
  }
  if (!customElements.get('blankabric-fabric-directory')) customElements.define('blankabric-fabric-directory', FabricDirectory);
}
